/**
 * VendingContractsPage — v2.5.912
 *
 * Contract Builder for the Enterprise Vending module. Exposes the rich
 * RichContractPayload schema behind a tabbed UI, seeded with 12 rental-term
 * templates (mirrors backend RENTAL_TEMPLATES in vending_enterprise_seed).
 *
 * Also drives the self-hosted Konnect-Sign flow:
 *   • Generate Document → renders the HTML via /generate-document
 *   • Add signatory → returns a sign-link
 *   • Signers open /vending/contracts/sign/:token → type name → contract auto-activates
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, ArrowLeft, Plus, RefreshCw, Loader2, ChevronRight,
  Sparkles, FileSignature, Copy, ExternalLink, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending`;

// Twelve rental-term templates — verbatim mirror of the backend seed so the UI
// picker matches exactly what /enterprise-reseed generates.
export const RENTAL_TEMPLATES = [
  { label: 'Free space, no rent',
    terms: [{ kind: 'free', fixed_amount: 0, percent: 0, tiers: [], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 0, payout_currency: 'PHP' }] },
  { label: 'Electricity-only reimbursement',
    terms: [{ kind: 'electricity_only', fixed_amount: 800, percent: 0, tiers: [], covers_electricity: true, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 15, payout_currency: 'PHP' }] },
  { label: 'Flat PHP 3,000 monthly rent',
    terms: [{ kind: 'fixed_monthly', fixed_amount: 3000, percent: 0, tiers: [], covers_electricity: true, covers_water: false, covers_internet: false, minimum_monthly_payout: 3000, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: 'Flat 10% of gross sales',
    terms: [{ kind: 'fixed_percent', fixed_amount: 0, percent: 10, tiers: [], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: 'Progressive 5-10% by monthly gross',
    terms: [{ kind: 'progressive_percent', fixed_amount: 0, percent: 0,
      tiers: [
        { min_amount: 0, max_amount: 20000, pct: 5, label: 'PHP 0 – 20,000' },
        { min_amount: 20000, max_amount: 50000, pct: 7.5, label: 'PHP 20,000 – 50,000' },
        { min_amount: 50000, max_amount: null, pct: 10, label: 'Above PHP 50,000' },
      ], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: 'MG: higher of PHP 2,500 or 8%',
    terms: [{ kind: 'minimum_guarantee', fixed_amount: 2500, percent: 8, tiers: [], covers_electricity: true, covers_water: false, covers_internet: false, minimum_monthly_payout: 2500, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: '100% first PHP 5k, 15% above',
    terms: [{ kind: 'revenue_share_stepped', fixed_amount: 0, percent: 0,
      tiers: [
        { min_amount: 0, max_amount: 5000, pct: 100, label: 'First PHP 5,000 to host' },
        { min_amount: 5000, max_amount: null, pct: 15, label: 'Above PHP 5,000' },
      ], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: 'PHP 1,500 base + 5% variable',
    terms: [
      { kind: 'fixed_monthly', fixed_amount: 1500, percent: 0, tiers: [], covers_electricity: true, covers_water: false, covers_internet: false, minimum_monthly_payout: 1500, grace_period_days: 5, payout_currency: 'PHP' },
      { kind: 'fixed_percent', fixed_amount: 0, percent: 5, tiers: [], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' },
    ] },
  { label: 'Product-supply-only (host retails)',
    terms: [{ kind: 'product_supply_only', fixed_amount: 0, percent: 0, tiers: [], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 15, payout_currency: 'PHP' }] },
  { label: 'Sponsorship — host pays PHP 5,000/mo',
    terms: [{ kind: 'sponsorship', fixed_amount: 5000, percent: 0, tiers: [], covers_electricity: true, covers_water: false, covers_internet: false, minimum_monthly_payout: 5000, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: '5-tier progressive (aggressive)',
    terms: [{ kind: 'progressive_percent', fixed_amount: 0, percent: 0,
      tiers: [
        { min_amount: 0, max_amount: 10000, pct: 3, label: 'PHP 0-10k' },
        { min_amount: 10000, max_amount: 25000, pct: 6, label: 'PHP 10k-25k' },
        { min_amount: 25000, max_amount: 50000, pct: 9, label: 'PHP 25k-50k' },
        { min_amount: 50000, max_amount: 100000, pct: 12, label: 'PHP 50k-100k' },
        { min_amount: 100000, max_amount: null, pct: 15, label: 'Above PHP 100k' },
      ], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' }] },
  { label: 'Fixed base + progressive stack',
    terms: [
      { kind: 'fixed_monthly', fixed_amount: 1000, percent: 0, tiers: [], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 1000, grace_period_days: 5, payout_currency: 'PHP' },
      { kind: 'progressive_percent', fixed_amount: 0, percent: 0,
        tiers: [
          { min_amount: 0, max_amount: 30000, pct: 4, label: 'PHP 0-30k' },
          { min_amount: 30000, max_amount: null, pct: 8, label: 'Above PHP 30k' },
        ], covers_electricity: false, covers_water: false, covers_internet: false, minimum_monthly_payout: 0, grace_period_days: 5, payout_currency: 'PHP' },
    ] },
];

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  under_review: 'bg-sky-100 text-sky-800 border-sky-200',
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  expired: 'bg-amber-100 text-amber-800 border-amber-200',
  terminated: 'bg-rose-100 text-rose-800 border-rose-200',
};

function blankContract() {
  return {
    client_id: '',
    location_ids: [],
    contract_number: '',
    title: '',
    starts_on: new Date().toISOString().slice(0, 10),
    ends_on: '',
    auto_renew: true,
    renewal_notice_days: 60,
    termination_notice_days: 90,
    billing_model: 'paying_vend',
    rental_terms: RENTAL_TEMPLATES[3].terms, // default: 10% flat
    max_retail_markup_pct: 100.0,
    approved_categories: ['snack', 'cold_drink', 'hot_beverage'],
    excluded_categories: [],
    price_change_notice_days: 30,
    sla_response_hours: 24,
    sla_restock_days: 7,
    sla_uptime_pct: 95.0,
    penalty_per_downtime_day: 0,
    payout_schedule: 'monthly',
    payout_day_of_month: 15,
    payout_method: 'bank_transfer',
    payout_currency: 'PHP',
    governing_law: 'Republic of the Philippines',
    dispute_resolution: 'Arbitration in Metro Manila',
    exclusivity: 'on_premises',
    non_compete_months: 0,
    confidentiality_years: 3,
    signatories: [],
    special_clauses: '',
    status: 'draft',
    tags: [],
    notes: '',
  };
}

export default function VendingContractsPage() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // full contract doc when open
  const [showBuilder, setShowBuilder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl, lo] = await Promise.all([
        axios.get(`${API}/enterprise/contracts`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/locations`),
      ]);
      setContracts(c.data || []);
      setClients(cl.data?.items || []);
      setLocations(lo.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load contracts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="vending-contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => navigate('/vending')} className="text-xs text-gray-500 hover:text-gray-700 mb-1 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to Vending Fleet
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="contracts-title">Vending Contracts</h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] sm:text-xs">Builder + E-Sign</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Draft, generate, and e-sign master vending services agreements with 12 rental structures.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-9" data-testid="contracts-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => { setEditing(blankContract()); setShowBuilder(true); }} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs sm:text-sm" data-testid="contracts-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> New contract
          </Button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading contracts…</div>}
      {!loading && contracts.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-gray-500">
          No contracts yet. <button className="text-emerald-700 underline" onClick={() => { setEditing(blankContract()); setShowBuilder(true); }}>Draft your first one</button>.
        </CardContent></Card>
      )}
      <div className="space-y-2">
        {contracts.map(c => {
          const cl = clients.find(x => x.id === c.client_id);
          const signedCount = (c.signatories || []).filter(s => s.signed_at).length;
          return (
            <Card key={c.id} data-testid={`contract-${c.id}`} onClick={() => { setEditing(c); setShowBuilder(true); }} className="cursor-pointer hover:border-emerald-300 transition-colors">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="font-semibold text-sm sm:text-base break-words min-w-0">{c.title || c.contract_number || 'Untitled contract'}</div>
                      <Badge variant="outline" className={`text-[10px] sm:text-xs ${STATUS_STYLES[c.status] || ''}`}>{c.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px] sm:text-xs font-mono">{c.contract_number}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{cl?.name || c.client_id?.slice(0, 8)}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{c.billing_model}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {(c.rental_terms || [])[0]?.kind || 'no terms'}
                      </Badge>
                      {c.generated_document_html && <Badge variant="outline" className="text-[10px] sm:text-xs bg-emerald-50 border-emerald-200 text-emerald-700">📄 Doc ready</Badge>}
                      {(c.signatories || []).length > 0 && (
                        <Badge variant="outline" className={`text-[10px] sm:text-xs ${signedCount === (c.signatories || []).length ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                          {signedCount}/{(c.signatories || []).length} signed
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] sm:text-xs text-gray-500 mt-2">
                      {c.starts_on} — {c.ends_on || 'ongoing'} · Payout {c.payout_schedule} via {c.payout_method}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center hidden sm:block" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showBuilder && (
        <ContractBuilderDialog
          contract={editing}
          clients={clients}
          locations={locations}
          onClose={() => { setShowBuilder(false); setEditing(null); }}
          onSaved={() => { setShowBuilder(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Contract Builder Dialog — 4 tabs: Basics / Rental Terms / Legal / Sign     */
/* ═══════════════════════════════════════════════════════════════════════════ */
function ContractBuilderDialog({ contract, clients, locations, onClose, onSaved }) {
  const [form, setForm] = useState({ ...blankContract(), ...contract });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('basics');
  const [doc, setDoc] = useState(null); // generated html preview
  const isNew = !form.id;

  const patch = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const availableLocations = locations.filter(l => !form.client_id || l.client_id === form.client_id);

  const applyTemplate = (idx) => {
    if (idx === '' || idx == null) return;
    const tpl = RENTAL_TEMPLATES[Number(idx)];
    if (!tpl) return;
    patch('rental_terms', JSON.parse(JSON.stringify(tpl.terms)));
    toast.success(`Applied: ${tpl.label}`);
  };

  const save = async () => {
    if (!form.client_id) { toast.error('Select a client first'); setTab('basics'); return; }
    if (!form.title) { toast.error('Contract title required'); setTab('basics'); return; }
    setBusy(true);
    try {
      const url = isNew ? `${API}/enterprise/contracts` : `${API}/enterprise/contracts/${form.id}`;
      const method = isNew ? 'post' : 'patch';
      const payload = { ...form };
      ['id', 'tenant_id', 'created_at', 'updated_at', 'created_by', '_id', 'generated_document_html'].forEach(k => delete payload[k]);
      const res = await axios[method](url, payload);
      toast.success(isNew ? 'Contract created' : 'Contract updated');
      // Persist ID from server for subsequent generate/sign actions
      setForm(f => ({ ...f, id: res.data?.id || f.id }));
      if (isNew) {
        // Stay in dialog so user can generate doc / add signatories
        setTab('sign');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const generateDoc = async () => {
    if (!form.id) { await save(); if (!form.id) return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/enterprise/contracts/${form.id}/generate-document`);
      setDoc(data.html || '');
      toast.success('Contract document generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    } finally { setBusy(false); }
  };

  const addSignatory = async (payload) => {
    if (!form.id) { toast.error('Save the contract first'); return; }
    try {
      const { data } = await axios.post(`${API}/enterprise/contracts/${form.id}/signatories`, payload);
      setForm(f => ({ ...f, signatories: [...(f.signatories || []), data.signatory] }));
      toast.success('Sign link created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add signatory');
    }
  };

  return (
    <Dialog open={true} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="contract-builder-dialog">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {isNew ? 'New Contract' : `Edit — ${form.contract_number || form.title}`}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="space-y-3">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="basics" className="text-xs sm:text-sm" data-testid="tab-basics">Basics</TabsTrigger>
            <TabsTrigger value="rental" className="text-xs sm:text-sm" data-testid="tab-rental">Rental Terms</TabsTrigger>
            <TabsTrigger value="legal" className="text-xs sm:text-sm" data-testid="tab-legal">SLA & Legal</TabsTrigger>
            <TabsTrigger value="sign" className="text-xs sm:text-sm" data-testid="tab-sign">Generate & Sign</TabsTrigger>
          </TabsList>

          {/* ─── BASICS TAB ─────────────────────────────────────────── */}
          <TabsContent value="basics" className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Field label="Client *">
                <Select value={form.client_id || ''} onValueChange={v => patch('client_id', v)}>
                  <SelectTrigger data-testid="contract-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Contract # (auto if blank)">
                <Input value={form.contract_number || ''} onChange={e => patch('contract_number', e.target.value)} data-testid="contract-number" />
              </Field>
            </div>
            <Field label="Contract title *">
              <Input value={form.title || ''} onChange={e => patch('title', e.target.value)} placeholder="Master Vending Services Agreement — X" data-testid="contract-title" />
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Field label="Starts on"><Input type="date" value={form.starts_on || ''} onChange={e => patch('starts_on', e.target.value)} /></Field>
              <Field label="Ends on"><Input type="date" value={form.ends_on || ''} onChange={e => patch('ends_on', e.target.value)} /></Field>
              <Field label="Renewal notice (d)"><Input type="number" value={form.renewal_notice_days ?? 60} onChange={e => patch('renewal_notice_days', parseInt(e.target.value, 10) || 0)} /></Field>
              <Field label="Termination notice (d)"><Input type="number" value={form.termination_notice_days ?? 90} onChange={e => patch('termination_notice_days', parseInt(e.target.value, 10) || 0)} /></Field>
            </div>
            <Field label="Billing model">
              <Select value={form.billing_model || 'paying_vend'} onValueChange={v => patch('billing_model', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paying_vend">Paying vend (end-user pays)</SelectItem>
                  <SelectItem value="freevend">Freevend (client subsidises)</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="consignment">Consignment</SelectItem>
                  <SelectItem value="operator_owned">Operator-owned</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={`Locations covered (${(form.location_ids || []).length})`}>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {availableLocations.length === 0 && <div className="text-xs text-gray-500 italic">No locations for this client yet.</div>}
                {availableLocations.map(l => (
                  <label key={l.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.location_ids || []).includes(l.id)}
                      onChange={e => {
                        const on = e.target.checked;
                        setForm(f => ({
                          ...f,
                          location_ids: on ? [...(f.location_ids || []), l.id] : (f.location_ids || []).filter(x => x !== l.id),
                        }));
                      }}
                    />
                    <span>{l.name}{l.city ? ` · ${l.city}` : ''}</span>
                  </label>
                ))}
              </div>
            </Field>
          </TabsContent>

          {/* ─── RENTAL TERMS TAB ───────────────────────────────────── */}
          <TabsContent value="rental" className="space-y-3">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-3 space-y-2">
                <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5" /> Apply a template
                </div>
                <Select onValueChange={applyTemplate} value="">
                  <SelectTrigger className="bg-white" data-testid="template-select"><SelectValue placeholder="Pick from 12 industry templates…" /></SelectTrigger>
                  <SelectContent>
                    {RENTAL_TEMPLATES.map((t, i) => <SelectItem key={i} value={String(i)}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-emerald-800">Applying a template overwrites the current rental term stack. You can then tweak amounts and %s below.</div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="text-xs font-semibold text-gray-700 uppercase">Rental term stack ({(form.rental_terms || []).length})</div>
                <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, rental_terms: [...(f.rental_terms || []), { kind: 'fixed_percent', percent: 5, fixed_amount: 0, tiers: [], payout_currency: 'PHP' }] }))} className="h-7 text-[11px]">
                  <Plus className="h-3 w-3 mr-1" /> Add term
                </Button>
              </div>
              {(form.rental_terms || []).map((t, i) => (
                <Card key={i}>
                  <CardContent className="p-2 sm:p-3 space-y-2">
                    <div className="flex justify-between items-start gap-2">
                      <Select value={t.kind} onValueChange={v => setForm(f => { const nt = [...(f.rental_terms || [])]; nt[i] = { ...nt[i], kind: v }; return { ...f, rental_terms: nt }; })}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid={`term-${i}-kind`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="electricity_only">Electricity only</SelectItem>
                          <SelectItem value="fixed_monthly">Fixed monthly</SelectItem>
                          <SelectItem value="fixed_percent">Fixed %</SelectItem>
                          <SelectItem value="progressive_percent">Progressive %</SelectItem>
                          <SelectItem value="minimum_guarantee">Minimum guarantee</SelectItem>
                          <SelectItem value="revenue_share_stepped">Revenue share stepped</SelectItem>
                          <SelectItem value="product_supply_only">Product-supply-only</SelectItem>
                          <SelectItem value="sponsorship">Sponsorship</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, rental_terms: (f.rental_terms || []).filter((_, j) => j !== i) }))} className="h-8 text-rose-600 hover:text-rose-800">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Field label="Fixed amount"><Input type="number" step="0.01" value={t.fixed_amount || 0} onChange={e => setForm(f => { const nt = [...f.rental_terms]; nt[i] = { ...nt[i], fixed_amount: parseFloat(e.target.value) || 0 }; return { ...f, rental_terms: nt }; })} /></Field>
                      <Field label="Percent"><Input type="number" step="0.01" value={t.percent || 0} onChange={e => setForm(f => { const nt = [...f.rental_terms]; nt[i] = { ...nt[i], percent: parseFloat(e.target.value) || 0 }; return { ...f, rental_terms: nt }; })} /></Field>
                      <Field label="Currency"><Input maxLength={3} value={t.payout_currency || 'PHP'} onChange={e => setForm(f => { const nt = [...f.rental_terms]; nt[i] = { ...nt[i], payout_currency: e.target.value.toUpperCase() }; return { ...f, rental_terms: nt }; })} /></Field>
                      <Field label="Min payout"><Input type="number" step="0.01" value={t.minimum_monthly_payout || 0} onChange={e => setForm(f => { const nt = [...f.rental_terms]; nt[i] = { ...nt[i], minimum_monthly_payout: parseFloat(e.target.value) || 0 }; return { ...f, rental_terms: nt }; })} /></Field>
                    </div>
                    {(t.kind === 'progressive_percent' || t.kind === 'revenue_share_stepped') && (
                      <div className="text-[11px] text-gray-500 bg-gray-50 p-2 rounded">
                        {(t.tiers || []).length} tier(s) configured — apply a template to auto-populate, or leave as-is.
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-[11px]">
                      {['covers_electricity', 'covers_water', 'covers_internet'].map(k => (
                        <label key={k} className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={!!t[k]} onChange={e => setForm(f => { const nt = [...f.rental_terms]; nt[i] = { ...nt[i], [k]: e.target.checked }; return { ...f, rental_terms: nt }; })} />
                          <span>{k.replace('covers_', 'Op absorbs ')}</span>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gray-50">
              <CardContent className="p-3 space-y-2">
                <div className="text-xs font-semibold text-gray-700 uppercase">Payout schedule</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Field label="Schedule">
                    <Select value={form.payout_schedule || 'monthly'} onValueChange={v => patch('payout_schedule', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="semi_annual">Semi-annual</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="on_settlement">On settlement</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Day"><Input type="number" min="1" max="31" value={form.payout_day_of_month ?? 15} onChange={e => patch('payout_day_of_month', parseInt(e.target.value, 10) || 15)} /></Field>
                  <Field label="Method">
                    <Select value={form.payout_method || 'bank_transfer'} onValueChange={v => patch('payout_method', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="gcash">GCash</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="wire">Wire</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Currency"><Input maxLength={3} value={form.payout_currency || 'PHP'} onChange={e => patch('payout_currency', e.target.value.toUpperCase())} /></Field>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── LEGAL / SLA TAB ────────────────────────────────────── */}
          <TabsContent value="legal" className="space-y-3">
            <Card><CardContent className="p-3 space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase">Service levels</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Field label="Response (hrs)"><Input type="number" value={form.sla_response_hours ?? 24} onChange={e => patch('sla_response_hours', parseInt(e.target.value, 10) || 0)} /></Field>
                <Field label="Restock (days)"><Input type="number" value={form.sla_restock_days ?? 7} onChange={e => patch('sla_restock_days', parseInt(e.target.value, 10) || 0)} /></Field>
                <Field label="Uptime %"><Input type="number" step="0.1" value={form.sla_uptime_pct ?? 95} onChange={e => patch('sla_uptime_pct', parseFloat(e.target.value) || 0)} /></Field>
                <Field label="Penalty / down-day"><Input type="number" step="0.01" value={form.penalty_per_downtime_day ?? 0} onChange={e => patch('penalty_per_downtime_day', parseFloat(e.target.value) || 0)} /></Field>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-3 space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase">Legal framework</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Governing law"><Input value={form.governing_law || ''} onChange={e => patch('governing_law', e.target.value)} /></Field>
                <Field label="Dispute resolution"><Input value={form.dispute_resolution || ''} onChange={e => patch('dispute_resolution', e.target.value)} /></Field>
                <Field label="Exclusivity">
                  <Select value={form.exclusivity || 'on_premises'} onValueChange={v => patch('exclusivity', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="on_premises">On premises</SelectItem>
                      <SelectItem value="on_premises_and_within_1km">On premises + 1km radius</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Non-compete (mo)"><Input type="number" value={form.non_compete_months ?? 0} onChange={e => patch('non_compete_months', parseInt(e.target.value, 10) || 0)} /></Field>
                <Field label="Confidentiality (yrs)"><Input type="number" value={form.confidentiality_years ?? 3} onChange={e => patch('confidentiality_years', parseInt(e.target.value, 10) || 0)} /></Field>
                <Field label="Max retail markup %"><Input type="number" step="0.01" value={form.max_retail_markup_pct ?? 100} onChange={e => patch('max_retail_markup_pct', parseFloat(e.target.value) || 0)} /></Field>
              </div>
              <Field label="Special clauses"><Textarea rows={3} value={form.special_clauses || ''} onChange={e => patch('special_clauses', e.target.value)} placeholder="Anything unusual — non-standard payment terms, marketing rights, etc." /></Field>
            </CardContent></Card>
          </TabsContent>

          {/* ─── GENERATE & SIGN TAB ────────────────────────────────── */}
          <TabsContent value="sign" className="space-y-3">
            {!form.id && (
              <Card className="border-amber-200 bg-amber-50"><CardContent className="p-3 text-xs text-amber-800">Save the contract first to generate the document and add signatories.</CardContent></Card>
            )}
            {form.id && (
              <>
                <Card><CardContent className="p-3 space-y-2">
                  <div className="text-xs font-semibold uppercase text-gray-700">Contract document</div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={generateDoc} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="generate-doc-btn">
                      {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating…</> : <><FileText className="h-3.5 w-3.5 mr-1" /> Generate contract document</>}
                    </Button>
                    {doc && (
                      <Button size="sm" variant="outline" onClick={() => {
                        const w = window.open('', '_blank');
                        if (w) { w.document.write(doc); w.document.close(); }
                      }} className="h-9 text-xs">
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in new tab
                      </Button>
                    )}
                  </div>
                  {doc && (
                    <iframe title="Contract preview" srcDoc={doc} className="w-full h-64 border rounded mt-2" />
                  )}
                </CardContent></Card>

                <SignatoriesPanel form={form} onAdd={addSignatory} />
              </>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center gap-2 pt-3 border-t">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-xs sm:text-sm" data-testid="contract-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs sm:text-sm" data-testid="contract-save-btn">
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : (isNew ? 'Create contract' : 'Save changes')}
          </Button>
        </div>
        {form.id && !onSaved.__called && (
          <button className="hidden" onClick={onSaved}>refresh</button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SignatoriesPanel({ form, onAdd }) {
  const [role, setRole] = useState('Client Authorized Signatory');
  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');

  return (
    <Card>
      <CardContent className="p-3 space-y-2">
        <div className="text-xs font-semibold uppercase text-gray-700">Signatories & Konnect Sign links</div>
        <div className="space-y-2">
          {(form.signatories || []).length === 0 && <div className="text-xs text-gray-500 italic">No signatories yet.</div>}
          {(form.signatories || []).map((s, i) => {
            const link = s.sign_token ? `${window.location.origin}/vending/contracts/sign/${s.sign_token}` : '';
            return (
              <div key={i} className="p-2 border rounded flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.full_name || <span className="italic text-gray-400">Unnamed</span>} · {s.role}</div>
                  <div className="text-[11px] text-gray-500 truncate">{s.email}</div>
                  {s.signed_at ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] mt-1">Signed {new Date(s.signed_at).toLocaleDateString()}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] mt-1 bg-amber-50 border-amber-200 text-amber-700">Awaiting signature</Badge>
                  )}
                </div>
                {link && !s.signed_at && (
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success('Sign link copied'); }} className="h-8 text-[11px]" data-testid={`copy-sign-link-${i}`}>
                    <Copy className="h-3 w-3 mr-1" /> Copy sign link
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <div className="p-2 border rounded bg-gray-50 space-y-2">
          <div className="text-xs font-semibold text-gray-700">Add signatory</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} className="h-8 text-xs" data-testid="signatory-name" />
            <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} className="h-8 text-xs" />
            <Input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="h-8 text-xs" data-testid="signatory-email" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Sales Representative">Sales Representative</SelectItem>
                <SelectItem value="General Manager">General Manager</SelectItem>
                <SelectItem value="Client Authorized Signatory">Client Authorized Signatory</SelectItem>
                <SelectItem value="Witness">Witness</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            onClick={() => {
              if (!fullName || !email) { toast.error('Name + email required'); return; }
              onAdd({ role, full_name: fullName, title, email });
              setFullName(''); setEmail(''); setTitle('');
            }}
            className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
            data-testid="add-signatory-btn"
          >
            <FileSignature className="h-3.5 w-3.5 mr-1" /> Add & generate sign link
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-gray-700 font-medium uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
